import { tenantConfig } from "@tenant-config";
import type { FAQItemType } from "@vega-core/models/faq.type";

/**
 * Fallback FAQ for tenants that do not define content.loginFaq in their config.
 * Uses helpEmail and companyName from runtime config.
 */
const getFallbackLPLoginFaqItems = (
  helpEmail: string,
  companyName: string,
): FAQItemType[] => [
  {
    title: "This is my first time signing in. What do I do?",
    body: `Check your emails for the invitation to the investor portal. If you already have an ${companyName} Okta account, you can use it to sign in. If not, you'll be guided through a quick setup process. Once complete, you'll have single sign-on access to the investor portal.`,
  },
  {
    title: "Why do I need to use Okta?",
    body: `Okta provides secure, simplified access to all ${companyName} applications. It lets you access all areas of the investor portal through one secure login. This means better security and convenience for you.`,
  },
  {
    title: "I can't access my account. I need help.",
    body: `First, check your email for any pending verification messages. If you're still having trouble, contact our support team at ${helpEmail}`,
  },
  {
    title: "I forgot my password.",
    body: "Click 'Sign In with Okta', then select 'Forgot password?' below the login fields. Follow the reset instructions to set up a new password.",
  },
];

/**
 * Returns LP login FAQ items from tenant config (content.loginFaq).
 * Resolved at build time per tenant (e.g. Apollo and Motive define their own;
 * see tenants/apollo/config.ts and tenants/motive/config.ts).
 * Tenants without loginFaq get a fallback FAQ using the given helpEmail and companyName.
 */
export const getLPLoginFaqItems = (
  helpEmail: string,
  companyName: string,
): FAQItemType[] => {
  const tenantFaq = tenantConfig.content.loginFaq;

  if (tenantFaq?.length) {
    return tenantFaq.map((item) => ({ title: item.title, body: item.body }));
  }

  return getFallbackLPLoginFaqItems(helpEmail, companyName);
};
